import React from 'react';
import Corridor from './Corridor.jsx';
import Segmente from './Segmente.jsx';
import { PRESETS_OPERATEUR, presetActif } from '../profils.js';
import { lisible } from '../format.js';
import Info from './Info.jsx';

/**
 * Bloc paramètres - replié par défaut.
 *
 * « Ouvrir ce bloc ne doit pas être nécessaire dans le cas courant : le profil
 * fournit déjà tout » (document 04). Le replier n'est donc pas une économie de
 * place, c'est la condition du parcours express en moins de dix secondes.
 */
export default function BlocParametres({ ouvert, onToggle, p, onChange, devise, children }) {
  // L'état retenu se déduit des valeurs, il n'est pas mémorisé : sans cela, un
  // préréglage resterait affiché comme actif alors que les trois champs qu'il
  // pose viennent d'être modifiés à la main juste en dessous.
  const actif = presetActif(p);
  /**
   * `unite` déclenche la relecture en clair sous le champ.
   *
   * Un champ numérique ne peut pas porter de séparateur de milliers - le
   * navigateur refuserait la valeur. « 1000000 » se lit donc en comptant les
   * zéros, et c'est exactement là qu'on se trompe d'un facteur dix sans le
   * voir. La relecture dit ce que la machine a compris.
   */
  const champ = (cle, etiquette, pas = '0.01', unite = '', info = null) => {
    const relecture = unite ? lisible(p[cle], unite) : null;
    return (
      <label style={{ display: 'block' }}>
        <span className="etiquette">{etiquette}{info}</span>
        <input
          type="number"
          step={pas}
          value={p[cle]}
          onChange={(e) => onChange({ [cle]: Number(e.target.value) })}
          className="champ"
          style={{ marginTop: 'var(--espace-1)' }}
        />
        {relecture && (
          // `aria-hidden` : un lecteur d'écran énonce déjà la valeur du champ,
          // l'entendre deux fois de suite gêne plus que ça n'aide.
          <span
            className="chiffre"
            aria-hidden="true"
            style={{
              display: 'block', marginTop: 'var(--espace-1)',
              fontSize: '0.8rem', color: 'var(--couleur-encre-douce)',
            }}
          >
            {relecture}
          </span>
        )}
      </label>
    );
  };

  return (
    <section className="carte">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={ouvert}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          width: '100%', padding: 0, border: 'none', background: 'none',
          color: 'var(--couleur-encre)', fontWeight: 600, fontSize: '0.95rem',
        }}
      >
        Ajuster les paramètres
        <span aria-hidden="true" style={{ color: 'var(--couleur-encre-douce)' }}>{ouvert ? '−' : '+'}</span>
      </button>

      {ouvert && (
        <div style={{ marginTop: 'var(--espace-4)', display: 'grid', gap: 'var(--espace-4)' }}>
          <Corridor
            officialRate={p.officialRate}
            rateOut={p.rateOut}
            rateIn={p.rateIn}
            onChange={onChange}
            devise={devise}
          />

          <div>
            <div className="etiquette" style={{ marginBottom: 'var(--espace-2)' }}>
              Frais de service
              <Info titre="Frais de service">
                Votre rémunération, celle que vous facturez au client. Une part
                fixe en euros plus un pourcentage du montant. Distincte des
                frais opérateur, que vous ne faites que subir.
              </Info>
            </div>
            <div className="grille-2">
              {champ('serviceFixed', 'Part fixe (EUR)', '0.01', 'EUR')}
              {champ('servicePercent', 'Part variable (%)', '0.1')}
            </div>
          </div>

          <div>
            <div className="etiquette" style={{ marginBottom: 'var(--espace-2)' }}>
              Frais opérateur
              <Info titre="Frais opérateur">
                Ce que prélève l’opérateur de transfert local, Mobile Money par
                exemple, sur chaque envoi. Vous ne le fixez pas : vous le
                constatez, et vous décidez seulement qui le supporte.
              </Info>
            </div>
            <div style={{ display: 'flex', gap: 'var(--espace-2)', marginBottom: 'var(--espace-2)', flexWrap: 'wrap', alignItems: 'center' }}>
              {PRESETS_OPERATEUR.map((preset) => {
                const retenu = actif === preset.nom;
                return (
                  <button
                    key={preset.nom}
                    type="button"
                    className="chip"
                    aria-pressed={retenu}
                    onClick={() => onChange({
                      operatorPercent: preset.operatorPercent,
                      operatorFixed: preset.operatorFixed,
                      operatorCap: preset.operatorCap,
                    })}
                  >
                    {preset.nom}
                  </button>
                );
              })}
              {/* Ne rien dire quand aucun préréglage ne correspond laisserait
                  croire à un oubli d'affichage plutôt qu'à un réglage manuel. */}
              {!actif && (
                <span style={{ fontSize: '0.8rem', color: 'var(--couleur-encre-douce)' }}>
                  Personnalisé
                </span>
              )}
            </div>
            <div className="grille-3">
              {champ('operatorPercent', 'Pourcentage', '0.01')}
              {champ('operatorFixed', `Fixe (${devise})`, '1', devise)}
              {/* Le plafond s'applique PAR transfert, jamais au total : c'est
                  lui qui déclenche la découpe en tranches. */}
              {champ('operatorCap', 'Plafond par transfert', '1000', devise,
                <Info titre="Plafond par transfert">
                  Montant maximal qu’un seul transfert peut porter chez
                  l’opérateur. Au-delà, l’envoi est découpé en plusieurs
                  transferts, et chacun paie ses propres frais. C’est ce qui
                  fait qu’un envoi deux fois plus gros peut coûter plus du
                  double.
                </Info>)}
            </div>
          </div>

          <Segmente
            etiquette="Qui porte les frais opérateur"
            aide={
              <Info titre="Qui porte les frais opérateur">
                Trois façons de traiter le prélèvement de l’opérateur.
                <strong> Refacturés</strong> : le client paie en plus, le
                destinataire touche le montant plein.
                <strong> Déduits</strong> : ils sont pris dans l’enveloppe, le
                destinataire touche moins.
                <strong> À ma charge</strong> : vous les absorbez, ils sortent
                de votre marge.
              </Info>
            }
            valeur={p.feeMode}
            onChange={(v) => onChange({ feeMode: v })}
            options={[
              { valeur: 'refacture', libelle: 'Refacturés', aide: 'Le client paie en plus, le destinataire touche le brut' },
              { valeur: 'deduit', libelle: 'Déduits', aide: 'Pris dans l’enveloppe, le destinataire touche moins' },
              { valeur: 'acharge', libelle: 'À ma charge', aide: 'Absorbés par la marge, personne d’autre ne paie' },
            ]}
          />

          {children}
        </div>
      )}
    </section>
  );
}
